import { useRef } from "react";
import { Box, IconButton, Tooltip, Typography, Divider, CircularProgress, TextField, InputAdornment } from "@mui/material";
import { Add, Remove, KeyboardArrowLeft, KeyboardArrowRight, KeyboardArrowUp, KeyboardArrowDown, Search } from "@mui/icons-material";
import { useTranslation } from "../../../localization/useTranslation";
import { useDocumentPreviewStyles } from "./DocumentPreview.styles";
import { useIsToolbarCompact } from "./DocumentPreview.hooks";
import { IDocumentNavigation, IDocumentPreviewToolbarAction } from "./DocumentPreview.types";
import { downloadIcon } from "../../icons";

export interface IDocumentPreviewToolbarProps {
    zoomLevel: number;
    zoomInputValue: string;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onZoomInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onZoomInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onZoomInputBlur: () => void;
    onDownload: () => void;
    isDownloading: boolean;
    pageSelector?: {
        currentPage: number;
        numPages: number;
        pageInputValue: string;
        onPageInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
        onPageInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
        onPageInputBlur: () => void;
        onPreviousPage: () => void;
        onNextPage: () => void;
    };
    documentNavigation?: IDocumentNavigation;
    toolbarAction?: IDocumentPreviewToolbarAction;
    searchProps?: {
        searchText: string;
        onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
        matchCount: number;
        currentMatchIndex: number;
        onPreviousMatch: () => void;
        onNextMatch: () => void;
    };
}

export default function DocumentPreviewToolbar(props: IDocumentPreviewToolbarProps) {
    const { translateString } = useTranslation();
    const toolbarRef = useRef<HTMLDivElement | null>(null);
    const isCompact = useIsToolbarCompact(toolbarRef);
    const styles = useDocumentPreviewStyles(isCompact);

    const showDocumentNavigation = props.documentNavigation && props.documentNavigation.totalDocuments > 1;

    const matchNavigation = props.searchProps && (
        <>
            <Typography variant="body2" sx={{ marginX: 1, whiteSpace: "nowrap" }}>
                {props.searchProps.matchCount > 0 ? `${props.searchProps.currentMatchIndex + 1} / ${props.searchProps.matchCount}` : "0 / 0"}
            </Typography>
            <IconButton size="small" onClick={props.searchProps.onPreviousMatch} disabled={props.searchProps.matchCount === 0}>
                <KeyboardArrowUp />
            </IconButton>
            <IconButton size="small" onClick={props.searchProps.onNextMatch} disabled={props.searchProps.matchCount === 0}>
                <KeyboardArrowDown />
            </IconButton>
        </>
    );

    const matchNavigationVisibility = { visibility: props.searchProps?.searchText ? "visible" : "hidden" } as const;

    return (
        <Box ref={toolbarRef} sx={styles.toolbar}>
            <Box sx={styles.toolbarSide}>
                {showDocumentNavigation && (
                    <Tooltip title={translateString("previous")}>
                        <IconButton size="small" onClick={props.documentNavigation!.onPreviousDocument}>
                            <KeyboardArrowLeft />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Box sx={styles.toolbarControls}>
                <Box sx={styles.toolbarCluster}>
                    <Tooltip title={translateString("zoomOut")}>
                        <span>
                            <IconButton size="small" onClick={props.onZoomOut} disabled={props.zoomLevel <= 0.25}>
                                <Remove />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <TextField
                        value={props.zoomInputValue}
                        onChange={props.onZoomInputChange}
                        onKeyDown={props.onZoomInputKeyDown}
                        onBlur={props.onZoomInputBlur}
                        size="small"
                        sx={{ width: 70, "& input": { fontSize: 14 } }}
                        slotProps={{
                            input: {
                                endAdornment: <Typography variant="body2">%</Typography>,
                            },
                        }}
                    />
                    <Tooltip title={translateString("zoomIn")}>
                        <span>
                            <IconButton size="small" onClick={props.onZoomIn} disabled={props.zoomLevel >= 3}>
                                <Add />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                {props.pageSelector && props.pageSelector.numPages > 0 && (
                    <Box sx={styles.toolbarCluster}>
                        {!isCompact && <Divider orientation="vertical" flexItem sx={{ marginX: 1 }} />}
                        <Tooltip title={translateString("previousPage")}>
                            <span>
                                <IconButton size="small" onClick={props.pageSelector.onPreviousPage} disabled={props.pageSelector.currentPage <= 1}>
                                    <KeyboardArrowLeft />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <TextField
                            value={props.pageSelector.pageInputValue}
                            onChange={props.pageSelector.onPageInputChange}
                            onKeyDown={props.pageSelector.onPageInputKeyDown}
                            onBlur={props.pageSelector.onPageInputBlur}
                            size="small"
                            sx={{ width: 60, "& input": { fontSize: 14, textAlign: "center" } }}
                        />
                        <Typography sx={{ marginX: 0.5, fontSize: 14, whiteSpace: "nowrap" }}>of {props.pageSelector.numPages}</Typography>
                        <Tooltip title={translateString("nextPage")}>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={props.pageSelector.onNextPage}
                                    disabled={props.pageSelector.currentPage >= props.pageSelector.numPages}
                                >
                                    <KeyboardArrowRight />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                )}

                <Box sx={styles.toolbarCluster}>
                    {!isCompact && <Divider orientation="vertical" flexItem sx={{ marginX: 1 }} />}
                    <Tooltip title={translateString("download")}>
                        <span>
                            <IconButton size="small" onClick={props.onDownload} disabled={props.isDownloading}>
                                {props.isDownloading ? <CircularProgress size={20} /> : downloadIcon}
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                {props.searchProps && (
                    <Box sx={styles.toolbarSearchCluster}>
                        {!isCompact && <Divider orientation="vertical" flexItem sx={{ marginX: 1 }} />}
                        <TextField
                            value={props.searchProps.searchText}
                            onChange={props.searchProps.onSearchChange}
                            size="small"
                            placeholder={translateString("search")}
                            sx={styles.toolbarSearchField}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search sx={{ color: "action.active", width: 24, height: 24 }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: isCompact ? (
                                        <InputAdornment position="end" sx={{ ...styles.toolbarSearchMatches, ...matchNavigationVisibility }}>
                                            {matchNavigation}
                                        </InputAdornment>
                                    ) : undefined,
                                },
                            }}
                        />
                        {!isCompact && <Box sx={{ ...styles.toolbarSearchMatches, ...matchNavigationVisibility }}>{matchNavigation}</Box>}
                    </Box>
                )}
            </Box>

            <Box sx={styles.toolbarSideEnd}>
                {props.toolbarAction && (
                    <Tooltip title={props.toolbarAction.tooltip}>
                        <span>
                            <IconButton size="small" onClick={props.toolbarAction.onClick} disabled={props.toolbarAction.disabled}>
                                {props.toolbarAction.icon}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {showDocumentNavigation && (
                    <Tooltip title={translateString("next")}>
                        <IconButton size="small" onClick={props.documentNavigation!.onNextDocument}>
                            <KeyboardArrowRight />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
}
